import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';

// SVG Icons instead of Lucide React components
const icons = {
  success: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  ),
  error: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
  warning: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  ),
  info: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  ),
  close: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  )
};

const NotificationDisplay: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();

  if (notifications.length === 0) return null;

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return icons.success;
      case 'error':
        return icons.error;
      case 'warning':
        return icons.warning;
      case 'info':
      default:
        return icons.info;
    }
  };

  // Opaque token surface in both themes; the type shows in the icon and a
  // 1px border, never in a tinted translucent fill (which let the page bleed
  // through in dark mode and made the text unreadable).
  const getBorderColor = (type: string) => {
    switch (type) {
      case 'success': return 'border-emerald-300 dark:border-emerald-700';
      case 'error': return 'border-red-300 dark:border-red-700';
      case 'warning': return 'border-amber-300 dark:border-amber-700';
      case 'info':
      default: return 'border-blue-300 dark:border-blue-700';
    }
  };

  return (
    // aria-live so VoiceOver announces toasts: this is the app-wide feedback
    // surface ("Foto sparat", sync errors), and without a live region every
    // one of those messages was silent to screen readers.
    <div
      className="fixed top-3 right-3 lg:right-auto lg:left-1/2 lg:-translate-x-1/2 lg:top-5 z-50 flex flex-col gap-2 max-w-xs lg:max-w-md lg:w-full"
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      {notifications.map((notification) => (
        <div
          key={notification.id}
          // `animate-fade-in`, not `animate-fadeIn` — the kebab-case name is
          // what tailwind.config.js defines, so the camelCase class emitted
          // nothing and toasts appeared with no animation at all.
          className={`rounded-ios-card border bg-[var(--bg-surface)] shadow-ios-elevated px-3.5 py-3 ${getBorderColor(notification.type)} transition-all duration-300 animate-fade-in`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-2">
              {getIcon(notification.type)}
            </div>
            <div className="flex-1">
              {notification.title && (
                <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">{notification.title}</h4>
              )}
              <p className="text-sm leading-snug text-[var(--text-primary)]">{notification.message}</p>
            </div>
            <button
              onClick={() => removeNotification(notification.id)}
              aria-label="Stäng meddelande"
              className="ml-3 -mr-1.5 -mt-1 h-[32px] w-[32px] flex items-center justify-center rounded-ios-control text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition"
            >
              {icons.close}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationDisplay; 